import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import type { OsobyFyzickaOsobaDto } from '@gov-nx/api';
import {
	countriesQuery,
	sendComplaintQuery,
	useAxiosInstance,
	usePoMutation,
	usePoQuery,
} from '@gov-nx/api';
import { GovError } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	useAddressSearch,
	usePersonCommunicationData,
	useProcessControl,
	useServiceAuthorization,
} from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import { type Nullable } from '@gov-nx/core/types';
import type { PageCode } from '../../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import { getCleanedChangedValues } from './getCleanedChangedValues';
import type {
	FormData,
	ProfileROBComplaintContext,
	ProfileRobComplaintProcessControl,
} from './context.types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

const ProfilROBReklamaceContext =
	createContext<Nullable<ProfileROBComplaintContext>>(null);

interface ProfilROBComplaintContextProviderProps {
	children: React.ReactNode;
	code: PageCode;
	data: Nullable<OsobyFyzickaOsobaDto>;
	onComplaintSubmit?: () => void;
}

const AUTHORIZATION_SERVICE_NAME = 'PODANI_REKLAMACE_ROB';

export function ProfilROBReklamaceContextProvider({
	children,
	code,
	data,
	onComplaintSubmit,
}: ProfilROBComplaintContextProviderProps) {
	const { setControls, controls } =
		useProcessControl<ProfileRobComplaintProcessControl>({
			initialDataError: null,
			initialCountryError: null,
			displaySearchAddressLoading: false,
			displaySearchAddress: false,
			displaySearchDeliveryAddressLoading: false,
			displaySearchDeliveryAddress: false,
			displayBirthPlace: false,
		});

	const personCommunicationData = usePersonCommunicationData();
	const { toastMessageSuccess } = useMessageEvents();
	const { t } = useLocale(code);
	const { axiosInstance } = useAxiosInstance();
	const authContext = useAutorizaceContext();
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const query = usePoQuery({
		queryKey: ['profile-rob-countries'],
		queryFn: () => countriesQuery(axiosInstance),
		onError: async (error: Error) => {
			setControls({ initialCountryError: new GovError(error.message) });
		},
	});

	const formDefinition = FormInstance({
		code,
		data,
		email: personCommunicationData.emailVerified,
		phone: personCommunicationData.phoneVerified,
		countries: query.data?.seznam ?? [],
		controls,
		isLowAuthorization,
	});

	const {
		form: { formMethods },
	} = formDefinition;

	const { address, deliveryAddress, mainQuery, deliveryAddressQuery } =
		useAddressSearch({
			formMethods,
			onSuccess: () =>
				setControls({
					displaySearchAddressLoading: false,
					displaySearchDeliveryAddressLoading: false,
				}),
			onError: () =>
				setControls({
					displaySearchAddressLoading: false,
					displaySearchDeliveryAddressLoading: false,
				}),
		});

	const newAddress = formMethods.watch('newAddress');
	const newDeliveryAddress = formMethods.watch('newDeliveryAddress');

	useEffect(() => {
		const selectedOption = address.find((option) => option.kod === newAddress);
		if (selectedOption && address.length > 0) {
			formMethods.setValue('adresaPobytu', selectedOption.adresaText || '');
		}
	}, [newAddress, address, formMethods]);

	useEffect(() => {
		const selectedOption = deliveryAddress.find(
			(option) => option.kod === newDeliveryAddress
		);
		if (selectedOption && deliveryAddress.length > 0) {
			formMethods.setValue('adresaDorucovaci', selectedOption.adresaText || '');
		}
		if (newDeliveryAddress === t('formular.adresa.neni-evidovana')) {
			formMethods.setValue(
				'adresaDorucovaci',
				t('formular.adresa.neni-evidovana') || ''
			);
		}
	}, [newDeliveryAddress, deliveryAddress, formMethods, t]);

	const newPlaceOfBirth = formMethods.watch('newPlaceOfBirth');

	const submitMutation = usePoMutation({
		mutationFn: async (data: FormData) => {
			const prepared = prepareSubmitData(
				newAddress,
				newDeliveryAddress,
				newPlaceOfBirth
			)(data);
			return sendComplaintQuery(axiosInstance, prepared);
		},
		onError: (error) => {
			setControls({ processError: error, processLoading: false });
		},
		onSuccess: async (data) => {
			setControls({ processLoading: false });
			formMethods.reset();
			onComplaintSubmit?.();

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('formular.zprava.odeslano'));
			};

			if (authContext) {
				authContext?.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const [initialFormValues, setInitialFormValues] = useState<FormData | null>(
		null
	);

	useEffect(() => {
		setInitialFormValues(formMethods.getValues());
	}, [formMethods]);

	const resetBirthPlaceSearchFields = () => {
		formMethods.resetField('stat');
		formMethods.resetField('newPlaceOfBirth');
	};

	const resetAddressSearchFields = () => {
		formMethods.resetField('nazevObce');
		formMethods.resetField('nazevUlice');
		formMethods.resetField('cislo');
	};

	const resetDeliveryAddressSearchFields = () => {
		formMethods.resetField('nazevObceDorucovaciAdresa');
		formMethods.resetField('nazevUliceDorucovaciAdresa');
		formMethods.resetField('cisloDorucovaciAdresa');
	};

	const handleSubmit = useCallback(async () => {
		setControls({ processError: null, processLoading: true });
		const changedValues: FormData = {};
		for (const fieldName in initialFormValues) {
			const initialValue = initialFormValues[fieldName as keyof FormData];
			const currentValue =
				formMethods.getValues()[
					fieldName === 'mistoNarozeni'
						? 'newPlaceOfBirth'
						: (fieldName as keyof FormData)
				];
			const trimmedCurrentValue =
				typeof currentValue === 'string' ? currentValue.trim() : currentValue;

			if (
				fieldName === 'email' ||
				fieldName === 'telefonniCislo' ||
				initialValue !== trimmedCurrentValue
			) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				changedValues[fieldName as keyof FormData] = trimmedCurrentValue;
			}
		}
		if (Object.keys(changedValues).length > 0) {
			const cleanedChangedValues = getCleanedChangedValues({
				changedValues,
				data,
			});
			submitMutation.mutate(cleanedChangedValues);
		} else {
			setControls({ processLoading: false });
		}
	}, [setControls, initialFormValues, formMethods, data, submitMutation]);

	const onSubmit = formMethods.handleSubmit(
		handleSubmit,
		personCommunicationData.onFormValidated
	);

	return (
		<ProfilROBReklamaceContext.Provider
			value={{
				controls: {
					...controls,
					initialLoading: query.isLoading,
				},
				setControls,
				formDefinition,
				data,
				country: query.data?.seznam ?? [],
				address,
				mainQuery,
				deliveryAddress,
				deliveryAddressQuery,
				personCommunicationData,
				resetAddressSearchFields,
				resetDeliveryAddressSearchFields,
				resetBirthPlaceSearchFields,
				onSubmit,
				disableSubmitButton: submitMutation.isSuccess,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ProfilROBReklamaceContext.Provider>
	);
}

export const useProfilROBReklamaceContextInstance =
	(): ProfileROBComplaintContext =>
		useContext(ProfilROBReklamaceContext) as ProfileROBComplaintContext;
